// @ts-nocheck
/* There are reported ts errors around loose types from highcharts that are cumbersome to silence with string templating.
  Can temporarily remove the no-check comment to see true errors and fix them if needed.
*/
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { TFunction } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { colors, fontWeights, fonts, spacing, caption } from 'ui/theme';
import { FC, useEffect, useRef } from 'react';
import {
  GraphHistory,
  TrendTypeEnum,
} from 'modules/intelligentInsights/brandAffinityInsights/types';
import { TimespanEnum } from 'modules/intelligentInsights/types';
import { localizeCompactNumber } from 'utils/l10n/number';
import {
  BrandLabelWeights,
  calculateMissingHistoryDates,
} from 'modules/intelligentInsights/brandAffinityInsights/contexts/dataManagement/dataManagement.utils';
import {
  useBrandAffinityTranslation,
  BRAND_AFFINITY_NAMES_TO_I18N_KEYS,
} from 'modules/domains/brandAffinity';
import { BRAND_AFFINITY_COLORS } from 'modules/domains';
import {
  formatCount,
  formatAsShortDate,
  formatAsFullDate,
  formatPercentagePoints,
} from 'modules/intelligentInsights/domain.utils';
import {
  AFFINITY_TRENDS_GRAPH_PERCENT_TITLE,
  AFFINITY_TRENDS_GRAPH_COUNT_TITLE,
} from 'modules/intelligentInsights/brandAffinityInsights/constants';

export const GraphTrendTypeTitles = {
  [TrendTypeEnum.Percent]: AFFINITY_TRENDS_GRAPH_PERCENT_TITLE,
  [TrendTypeEnum.Count]: AFFINITY_TRENDS_GRAPH_COUNT_TITLE,
};

export const createGraphOptions = (
  graphHistory: GraphHistory,
  graphTrendType: TrendTypeEnum,
  t: TFunction,
  selectedTimespan: TimespanEnum,
  compareDate = new Date(),
) => {
  const isPercentType = graphTrendType === TrendTypeEnum.Percent;
  let historyPoints = isPercentType
    ? cloneDeep(graphHistory.byPercent.historyPoints)
    : cloneDeep(graphHistory.byCount.historyPoints);

  const { remainingDates } = calculateMissingHistoryDates(
    graphHistory.categories[0],
    selectedTimespan,
    compareDate,
  );

  const adjustedCategories = graphHistory.categories.concat();
  if (!isEmpty(remainingDates) && !isEmpty(historyPoints)) {
    adjustedCategories.unshift(...remainingDates);
    const missingSeriesFiller = new Array(remainingDates.length).fill(null).map((x) => x);
    for (let i = 0; i < 5; i++) {
      historyPoints[i].unshift(...missingSeriesFiller);
    }
  }

  if (isPercentType) {
    historyPoints = historyPoints.map((historyPointLabels) =>
      historyPointLabels.map((point) => {
        if (point === null) return point;
        return Number(formatPercentagePoints(point));
      }),
    );
  }
  const series = historyPoints.map((historyPoint, index) => {
    const brandLabelWeight = BrandLabelWeights[index];
    return {
      brandLabel: t(BRAND_AFFINITY_NAMES_TO_I18N_KEYS[brandLabelWeight.label]),
      color: BRAND_AFFINITY_COLORS[brandLabelWeight.label],
      data: historyPoint,
      dateHeader: '',
      type: 'column',
    };
  });

  const axisCategories = adjustedCategories.map(formatAsShortDate);
  // No data will be available for prefixed date categories
  const toolTipCategories = graphHistory.categories.map(formatAsFullDate);

  return {
    reflow: false,
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    chart: {
      type: 'column',
      width: null,
      height: null,
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: axisCategories,
      labels: {
        style: {
          ...caption,
          fontFamily: fonts.sansSerif,
          color: colors.textSecondary,
        },
      },
    },
    yAxis: {
      maxPadding: 0,
      minRange: 1,
      gridLineDashStyle: 'ShortDash',
      min: 0,
      labels: {
        formatter: function yAxisFormatter() {
          return isPercentType ? `${this.value}%` : localizeCompactNumber(this.value);
        },
        style: {
          ...caption,
          fontWeight: fontWeights.medium,
          fontFamily: fonts.sansSerif,
          color: colors.textSecondary,
        },
      },
      title: {
        text: GraphTrendTypeTitles[graphTrendType],
        style: {
          ...caption,
          fontFamily: fonts.sansSerif,
          color: colors.textSecondary,
        },
      },
    },
    legend: {
      align: 'center',
      enabled: true,
      itemStyle: {
        ...caption,
        color: colors.textSecondary,
        fontWeight: fontWeights.medium,
        fontFamily: fonts.sansSerif,
      },
      labelFormatter: function labelFormatter() {
        return this.userOptions.brandLabel;
      },
    },
    plotOptions: {
      line: {
        dashStyle: 'ShortDash',
      },
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false,
        },
        pointWidth: 20,
        minPointLength: 3,
      },
    },
    series,
    tooltip: {
      borderColor: colors.grey80,
      borderRadius: 4,
      backgroundColor: colors.grey80,
      style: {
        fontFamily: fonts.sansSerif,
      },
      useHTML: true,
      formatter: function formatter() {
        return `<span style="color:${colors.grey25}">${toolTipCategories[this.point.x]}</span><br />
         <div style="display: flex; align-items: center;gap: ${spacing.half};"><span style="color:${
           this.point.color
         };font-size:12px;">\u25CF</span>
         <span style="color:${colors.white};">${this.point.series.userOptions.brandLabel}: ${
           isPercentType ? `${this.point.y}%` : formatCount(this.point.y)
         }</span>
         </div>`;
      },
    },
  };
};

export interface AffinityTrendsGraphProps {
  graphHistory: GraphHistory;
  graphTrendType: TrendTypeEnum;
  selectedTimespan: TimespanEnum;
}
export const AffinityTrendsGraph: FC<AffinityTrendsGraphProps> = ({
  graphHistory,
  graphTrendType,
  selectedTimespan,
}) => {
  const chartComponentRef = useRef(null);
  const { t } = useBrandAffinityTranslation();
  const options = createGraphOptions(graphHistory, graphTrendType, t, selectedTimespan);
  // Handle window resize event to trigger Highcharts reflow
  useEffect(() => {
    const handleResize = () => {
      if (chartComponentRef.current) {
        const { chart } = chartComponentRef.current;
        if (chart.container && window.innerWidth < 1280) {
          chart.setSize(window.innerWidth - 100, null);
        } else {
          chart.setSize('700', null);
        }
      }
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return (
    <HighchartsReact
      ref={chartComponentRef}
      highcharts={Highcharts}
      constructorType="chart"
      options={options as any}
    />
  );
};
